import { ReactNode, useId } from 'react';
import { X } from 'lucide-react';

interface ActionModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onSave: () => void;
  onCancel?: () => void;
  children: ReactNode;
  isDarkMode?: boolean;
  isSaving?: boolean;
  disableSave?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  size?: 'md' | 'lg' | 'xl';
}

function classNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(' ');
}

const sizeToWidth: Record<Exclude<ActionModalProps['size'], undefined>, string> = {
  md: 'max-w-2xl',
  lg: 'max-w-3xl',
  xl: 'max-w-5xl',
};

export default function ActionModal({
  isOpen,
  title,
  onClose,
  onSave,
  onCancel,
  children,
  isDarkMode = false,
  isSaving = false,
  disableSave = false,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  size = 'lg',
}: ActionModalProps) {
  const titleId = useId();
  const descriptionId = useId();

  if (!isOpen) {
    return null;
  }

  const handleCancel = () => {
    if (isSaving) {
      return;
    }
    (onCancel ?? onClose)();
  };

  const handleSave = () => {
    if (isSaving || disableSave) {
      return;
    }
    onSave();
  };

  return (
    <div
      className={classNames(
        'fixed inset-0 z-50 flex items-center justify-center px-4 py-8 backdrop-blur-sm',
        isDarkMode ? 'bg-slate-100/70' : 'bg-slate-900/70',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      onMouseDown={event => {
        if (!isSaving && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className={classNames(
          'w-full rounded-xl border shadow-2xl',
          sizeToWidth[size],
          isDarkMode
            ? 'border-slate-700 bg-slate-900 text-slate-100'
            : 'border-slate-200 bg-white text-slate-900',
        )}
        onMouseDown={event => {
          event.stopPropagation();
        }}
      >
        <div
          className={classNames(
            'flex items-center justify-between border-b px-6 py-4',
            isDarkMode ? 'border-slate-700' : 'border-slate-200',
          )}
        >
          <h2 id={titleId} className="text-lg font-semibold">
            {title}
          </h2>
          <button
            type="button"
            onClick={isSaving ? undefined : onClose}
            className={classNames(
              'rounded-full p-2 transition',
              isDarkMode ? 'text-slate-300 hover:bg-slate-800' : 'text-slate-500 hover:bg-slate-100',
              isSaving ? 'cursor-not-allowed opacity-60' : undefined,
            )}
            aria-label={`Close ${title} modal`}
            disabled={isSaving}
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div id={descriptionId} className="px-6 py-5">
          {children}
        </div>

        <div
          className={classNames(
            'flex items-center justify-end gap-3 border-t px-6 py-4',
            isDarkMode ? 'border-slate-700' : 'border-slate-200',
          )}
        >
          <button
            type="button"
            onClick={handleCancel}
            className={classNames(
              'rounded-full border px-4 py-2 text-sm font-semibold transition',
              isDarkMode
                ? 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100',
              isSaving ? 'cursor-not-allowed opacity-60 hover:bg-transparent' : undefined,
            )}
            disabled={isSaving}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleSave}
            className={classNames(
              'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white transition',
              isDarkMode
                ? 'bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/40'
                : 'bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400/60',
              isSaving ? 'cursor-not-allowed' : undefined,
            )}
            disabled={disableSave || isSaving}
          >
            {isSaving ? 'Saving…' : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
